// Faithful TS port of approved-cockpit.js svgSparkline: a CSP-clean inline-SVG
// sparkline with clamped reference lines (entry/stop/target). Returns markup
// rendered via dangerouslySetInnerHTML, reusing the existing .om-chart CSS.
const ESC: Record<string, string> = { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' };
function esc(s: string): string {
  return String(s).replace(/[&<>"']/g, (c) => ESC[c]);
}

export interface SparkRef {
  value: number;
  cls?: string;
}

export function svgSparkline(values: number[], opts: { empty?: string; refs?: SparkRef[] } = {}): string {
  const vals = (values || []).map(Number).filter((n) => Number.isFinite(n));
  if (vals.length < 2) {
    return `<div class="om-muted" style="padding:10px 2px">${esc(opts.empty || 'No history available yet.')}</div>`;
  }
  const W = 320;
  const H = 84;
  const pad = 5;
  const min = Math.min(...vals);
  const max = Math.max(...vals);
  const rng = max - min || 1;
  const X = (i: number) => pad + (i / (vals.length - 1)) * (W - 2 * pad);
  const Y = (v: number) => H - pad - ((v - min) / rng) * (H - 2 * pad);
  const line = vals.map((v, i) => (i ? 'L' : 'M') + X(i).toFixed(1) + ' ' + Y(v).toFixed(1)).join(' ');
  const area =
    'M' + X(0).toFixed(1) + ' ' + (H - pad) +
    ' ' + vals.map((v, i) => 'L' + X(i).toFixed(1) + ' ' + Y(v).toFixed(1)).join(' ') +
    ' L' + X(vals.length - 1).toFixed(1) + ' ' + (H - pad) + ' Z';
  const refs = (opts.refs || [])
    .map((r) => {
      const v = Number(r.value);
      if (!Number.isFinite(v) || v <= 0) return '';
      const cv = Math.max(min, Math.min(max, v)); // clamp out-of-range levels to the edge
      const edge = v > max ? ' edge-above' : v < min ? ' edge-below' : '';
      return `<line class="om-chart-ref ${r.cls || ''}${edge}" x1="${pad}" y1="${Y(cv).toFixed(1)}" x2="${W - pad}" y2="${Y(cv).toFixed(1)}"/>`;
    })
    .join('');
  const up = vals[vals.length - 1] >= vals[0];
  return (
    `<svg class="om-chart om-spark" viewBox="0 0 ${W} ${H}" xmlns="http://www.w3.org/2000/svg">` +
    `<path class="om-chart-area ${up ? 'up' : 'down'}" d="${area}"/>${refs}` +
    `<path class="om-chart-line ${up ? 'up' : 'down'}" d="${line}"/></svg>`
  );
}

// Format a price with sensible precision for axis labels.
function fmtPrice(v: number): string {
  const a = Math.abs(v);
  const d = a >= 1000 ? 0 : a >= 100 ? 1 : a >= 1 ? 2 : 4;
  return v.toFixed(d);
}

// Larger price chart WITH labeled axes — Y = price, X = trading days ago — plus light gridlines
// and a value label on each entry/stop/target line. Used by components/PriceChart.tsx. The tiny
// inline svgSparkline above is left untouched for the dense list/strip uses.
export function svgPriceChartWithAxes(values: number[], opts: { refs?: SparkRef[]; empty?: string } = {}): string {
  const vals = (values || []).map(Number).filter((n) => Number.isFinite(n));
  if (vals.length < 2) {
    return `<div class="om-muted" style="padding:10px 2px">${esc(opts.empty || 'No history available yet.')}</div>`;
  }
  const W = 360;
  const H = 150;
  const mL = 44; // left margin for Y (price) labels
  const mR = 50; // right margin for entry/stop/target value labels
  const mT = 8;
  const mB = 22; // bottom margin for X (days-ago) labels
  const plotW = W - mL - mR;
  const plotH = H - mT - mB;
  const n = vals.length;
  const min = Math.min(...vals);
  const max = Math.max(...vals);
  // Pad the value range slightly so the line isn't glued to the top/bottom edge.
  const padR = (max - min || Math.abs(max) || 1) * 0.06;
  const lo = min - padR;
  const hi = max + padR;
  const rng = hi - lo || 1;
  const X = (i: number) => mL + (i / (n - 1)) * plotW;
  const Y = (v: number) => mT + (1 - (v - lo) / rng) * plotH;

  const line = vals.map((v, i) => (i ? 'L' : 'M') + X(i).toFixed(1) + ' ' + Y(v).toFixed(1)).join(' ');
  const area =
    'M' + X(0).toFixed(1) + ' ' + (mT + plotH).toFixed(1) +
    ' ' + vals.map((v, i) => 'L' + X(i).toFixed(1) + ' ' + Y(v).toFixed(1)).join(' ') +
    ' L' + X(n - 1).toFixed(1) + ' ' + (mT + plotH).toFixed(1) + ' Z';
  const up = vals[n - 1] >= vals[0];

  // Y axis: 4 price gridlines + labels (top = hi .. bottom = lo).
  const yTicks = 4;
  let grid = '';
  let yLabels = '';
  for (let t = 0; t <= yTicks; t++) {
    const v = hi - (t / yTicks) * rng;
    const y = (mT + (t / yTicks) * plotH).toFixed(1);
    grid += `<line class="om-chart-grid" x1="${mL}" y1="${y}" x2="${mL + plotW}" y2="${y}"/>`;
    yLabels += `<text class="om-chart-axislabel y" x="${mL - 6}" y="${(Number(y) + 3).toFixed(1)}" text-anchor="end">${esc(fmtPrice(v))}</text>`;
  }

  // X axis: oldest (left) -> today (right), labeled in trading days ago.
  const axisY = mT + plotH;
  const xMarks: Array<{ i: number; label: string }> = [
    { i: 0, label: `${n - 1}d ago` },
    { i: Math.round((n - 1) / 2), label: `${Math.round((n - 1) / 2)}d` },
    { i: n - 1, label: 'today' },
  ];
  const xLabels = xMarks
    .map((m, k) => {
      const anchor = k === 0 ? 'start' : k === xMarks.length - 1 ? 'end' : 'middle';
      return `<text class="om-chart-axislabel x" x="${X(m.i).toFixed(1)}" y="${(axisY + 14).toFixed(1)}" text-anchor="${anchor}">${esc(m.label)}</text>`;
    })
    .join('');

  // Entry / stop / target reference lines, clamped into view, with a value label at the right.
  const refs = (opts.refs || [])
    .map((r) => {
      const v = Number(r.value);
      if (!Number.isFinite(v) || v <= 0) return '';
      const cv = Math.max(lo, Math.min(hi, v));
      const y = Y(cv).toFixed(1);
      const edge = v > hi ? ' edge-above' : v < lo ? ' edge-below' : '';
      return (
        `<line class="om-chart-ref ${r.cls || ''}${edge}" x1="${mL}" y1="${y}" x2="${mL + plotW}" y2="${y}"/>` +
        `<text class="om-chart-reflabel ${r.cls || ''}" x="${mL + plotW + 5}" y="${(Number(y) + 3).toFixed(1)}" text-anchor="start">${esc(fmtPrice(v))}</text>`
      );
    })
    .join('');

  return (
    `<svg class="om-chart om-price-axes" viewBox="0 0 ${W} ${H}" xmlns="http://www.w3.org/2000/svg">` +
    grid +
    `<path class="om-chart-area ${up ? 'up' : 'down'}" d="${area}"/>` +
    refs +
    `<path class="om-chart-line ${up ? 'up' : 'down'}" d="${line}"/>` +
    `<line class="om-chart-axis" x1="${mL}" y1="${mT}" x2="${mL}" y2="${axisY}"/>` +
    `<line class="om-chart-axis" x1="${mL}" y1="${axisY}" x2="${mL + plotW}" y2="${axisY}"/>` +
    yLabels +
    xLabels +
    `<text class="om-chart-axistitle" x="${(mL + plotW / 2).toFixed(1)}" y="${H - 2}" text-anchor="middle">trading days ago</text>` +
    `</svg>`
  );
}

export interface DonutSeg {
  value: number;
  cls?: string;
}

// Faithful port of svgDonut (the legacy had a `</text>` typo — fixed here).
export function svgDonut(segments: DonutSeg[], centerText?: string | null, emptyMsg?: string): string {
  const total = segments.reduce((s, x) => s + (Number(x.value) || 0), 0);
  if (!total) return `<div class="om-muted" style="padding:10px 2px">${esc(emptyMsg || 'No data yet.')}</div>`;
  const R = 46;
  const C = 2 * Math.PI * R;
  let off = 0;
  const ring = segments
    .map((s) => {
      const frac = (Number(s.value) || 0) / total;
      if (frac <= 0) return '';
      const seg =
        `<circle class="om-donut-seg ${s.cls || ''}" cx="60" cy="60" r="${R}" fill="none" stroke-width="13" ` +
        `stroke-dasharray="${(frac * C).toFixed(2)} ${((1 - frac) * C + 0.5).toFixed(2)}" ` +
        `stroke-dashoffset="${(-off * C).toFixed(2)}" transform="rotate(-90 60 60)"/>`;
      off += frac;
      return seg;
    })
    .join('');
  const center = centerText != null ? `<text class="om-donut-center" x="60" y="62" text-anchor="middle">${esc(centerText)}</text>` : '';
  return (
    `<svg class="om-chart om-donut" viewBox="0 0 120 120" xmlns="http://www.w3.org/2000/svg">` +
    `<circle class="om-donut-track" cx="60" cy="60" r="${R}" fill="none" stroke-width="13"/>${ring}${center}</svg>`
  );
}
